import { AuthCredentials, AuthResponse as ImportedAuthResponse, User } from '../types/auth';
import { apiClient } from '../api/apiClient';
import axios, { AxiosError } from 'axios';

interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

interface OTPResponse {
  message: string;
}

interface AuthResponse {
  user: User;
  token: string;
  shop_data: {
    shopify_domain: string;
    shop_origin: string;
    enable_password: boolean;
    country: string;
    all_shops: Array<{
      shopify_domain: string;
      shop_origin: string;
      enable_password: boolean;
      country: string;
      shop_token: string;
    }>;
  };
  mixpanel_data: Record<string, unknown>;
  needs_onboarding?: boolean;
}

interface GoogleOAuthResponse {
  code: string;
}

interface GoogleClient {
  requestCode: () => void;
}

declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleOAuthResponse) => Promise<AuthResponse>;
          }) => GoogleClient;
        };
      };
    };
  }
}

class AuthService {
  private static instance: AuthService;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private googleClient: GoogleClient | null = null;
  private initialized: boolean = false;

  private constructor() {}

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private initialize(): void {
    if (this.initialized) return;

    // Initialize tokens from localStorage only in browser environment
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('accessToken');
      this.refreshToken = localStorage.getItem('refreshToken');
      
      // Initialize Google client
      if (window.google) {
        this.googleClient = window.google.accounts.oauth2.initCodeClient({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
          scope: 'email profile',
          callback: this.handleGoogleCallback.bind(this)
        });
      }
    }

    this.initialized = true;
  }

  // Traditional email/password login
  public async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/api/login', {
        email,
        password
      });
      
      // Store the tokens
      this.setTokens(response.data.token, response.data.token);  // token is now a string
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw this.handleAuthError(error);
    }
  }

  // Passwordless OTP-based login
  public async requestOTP(email: string): Promise<OTPResponse> {
    try {
      console.log('Requesting OTP for email:', email);
      const response = await apiClient.post<OTPResponse>('/api/v1/user_shop_password_less_login/send_otp_passwordless_login', {
        email,
        theme_preference: 'light',
        sso_login: true
      });
      console.log('OTP request response:', response.data);
      return response.data;
    } catch (error) {
      console.error('OTP request error:', error);
      throw this.handleAuthError(error);
    }
  }

  public async verifyOTP(email: string, otp: string): Promise<AuthResponse> {
    try {
      console.log('Verifying OTP for email:', email);
      const response = await apiClient.post<AuthResponse>('/api/v1/user_shop_password_less_login/auth_with_otp', {
        email,
        otp
      });
      console.log('OTP verification response:', response.data);

      // Store the tokens using the setTokens method
      this.setTokens(response.data.token, response.data.token);  // token is now a string

      // Store user data
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Store shop data
      localStorage.setItem('shop_data', JSON.stringify(response.data.shop_data));

      // Store mixpanel data if available
      if (response.data.mixpanel_data) {
        localStorage.setItem('mixpanel_data', JSON.stringify(response.data.mixpanel_data));
      }

      // Store Shopify-specific data if available
      if (response.data.shop_data?.shopify_domain) {
        const domainKey = response.data.shop_data.shopify_domain.replace('.myshopify.com', '');
        localStorage.setItem('chargezenDomain', response.data.shop_data.shopify_domain);
        localStorage.setItem('currentShopDomain', response.data.shop_data.shopify_domain);
        localStorage.setItem(`${domainKey}_authToken`, response.data.token);
        localStorage.setItem(`${domainKey}_accessToken`, response.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('OTP verification error:', error);
      throw this.handleAuthError(error);
    }
  }


  // Google authentication
  public async loginWithGoogle(): Promise<void> {
    this.initialize();
    if (!this.googleClient) {
      throw new Error('Google client not initialized');
    }
    this.googleClient.requestCode();
  }

  private async handleGoogleCallback(response: GoogleOAuthResponse): Promise<AuthResponse> {
    try {
      const result = await apiClient.post<AuthResponse>('/api/google_auth', {
        id_token: response.code
      });
      
      // Store the tokens
      this.setTokens(result.data.token, result.data.token);  // token is now a string
      return result.data;
    } catch (error) {
      console.error('Google callback error:', error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<ApiError>;
        if (axiosError.response?.status === 401 && 
            axiosError.response?.data?.message === "No account found with this email. Please sign up first.") {
          throw new Error("No account found with this email. Please sign up first.");
        }
      }
      throw this.handleAuthError(error);
    }
  }

  public async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/api/v1/me');
      return response.data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw this.handleAuthError(error);
    }
  }

  public getToken(): string | null {
    this.initialize();
    return this.accessToken;
  }

  private setTokens(accessToken: string, refreshToken?: string): void {
    this.accessToken = accessToken;
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }
    if (typeof window !== 'undefined') {
      // Store in localStorage
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Store in cookies
      document.cookie = `token=${accessToken}; path=/`;
      if (refreshToken) {
        document.cookie = `refreshToken=${refreshToken}; path=/`;
      }
    }
  }

  public async logout(): Promise<void> {
    try {
      // No API call needed, just clear local storage
      this.accessToken = null;
      this.refreshToken = null;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  private handleAuthError(error: unknown): Error {
    console.error('Handling auth error:', error);
    
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      const message = axiosError.response?.data?.message || axiosError.message;
      const code = axiosError.response?.data?.code;
      const status = axiosError.response?.status;

      console.error('Axios error details:', {
        status,
        message,
        code,
        response: axiosError.response?.data
      });

      switch (status) {
        case 401:
          return new Error('Invalid credentials');
        case 403:
          return new Error('Access denied');
        case 404:
          return new Error('Email does not exist in system. Please sign up or contact customer support.');
        case 422:
          return new Error('Invalid OTP or OTP expired.');
        case 429:
          return new Error('Too many login attempts. Please try again later');
        default:
          return new Error(message || 'Authentication failed');
      }
    }
    return new Error('An unexpected error occurred');
  }
}

export const authService = AuthService.getInstance(); 